import { Link } from "react-router-dom";

/**
 * The oodel. wordmark, used everywhere.
 *
 * It is rendered as text (not an image) so colour adapts to the background:
 *  • <Logo />            → black letters, green dot (light backgrounds)
 *  • <Logo inverted />   → white letters, green dot (dark backgrounds)
 * The green dot colour is constant (#16A34A) on both variants.
 *
 * Props:
 *  • to       — react-router link target; pass `false` to render a non-link span.
 *  • inverted — switches letters to white. Use on dark/black surfaces.
 *  • size     — font-size scale: 'sm' | 'md' | 'lg' | 'xl'
 */
const SIZE_MAP = {
  sm: "text-base",
  md: "text-xl",
  lg: "text-2xl",
  xl: "text-3xl",
};

export default function Logo({ to = "/", inverted = false, size = "md", className = "", suffix = null, "data-testid": testId = "brand-logo" }) {
  const text = (
    // Explicit inline font-family override — the app's global Urdu mode
    // (`body.oodel-lang-ur` in index.css) switches the WHOLE page's
    // font-family to Noto Naskh Arabic so real Urdu text renders correctly.
    // Latin fonts render their own (different-looking) Latin glyph subset
    // too, so without this override the wordmark's letterforms visibly
    // change shape/weight the moment a patron switches to Urdu — the
    // brand mark must look identical in both languages. Inline style wins
    // over the body-level class regardless of load order.
    <span
      className={`font-black tracking-tighter leading-none ${SIZE_MAP[size] || SIZE_MAP.md} ${inverted ? "text-white" : "text-black"}`}
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
    >
      oodel<span style={{ color: "#16A34A" }}>.</span>
    </span>
  );
  const inner = suffix ? (
    <span className="flex items-baseline gap-3">
      {text}
      {suffix}
    </span>
  ) : text;
  if (to === false) return <span className={className} data-testid={testId}>{inner}</span>;
  return (
    <Link to={to} className={`inline-flex items-center ${className}`} data-testid={testId}>
      {inner}
    </Link>
  );
}
